import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  EmailValidationService,
  RegisterResponse,
} from '../../../services/email-validation.service';
import { LoginFlow } from '../../../types/auth.types';
import { LoginState, AccountDetectedState } from '../../../types/login.types';
import { LoginService } from '../../../services/login.service';
import { LogoHeaderComponent } from '../shared/logo-header/logo-header.component';
import { TipoVerificacionEnum } from '../../../constants/tipo-verificacion.enum';
import { ModoRegistroEnum } from '../../../constants/modo-registro.enum';
import { TipoProveedorOAuthEnum } from '../../../constants/tipo-proveedor-oauth.enum';
import { OtpBlockComponent } from '../otp-block/otp-block.component';
import { LoadingSpinnerComponent } from '../shared/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-account-detected',
  imports: [
    CommonModule,
    FormsModule,
    LogoHeaderComponent,
    OtpBlockComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './account-detected.component.html',
})
export class AccountDetectedComponent {
  currentState: AccountDetectedState | undefined | null;

  otpSent = signal<boolean>(false);
  countdown = signal<number>(30);
  idVerificacionVinculacion = signal<number>(0);

  isProcessingRegister = signal<boolean>(false);

  constructor(
    private emailValidationService: EmailValidationService,
    private loginService: LoginService
  ) {
    this.loginService.loginState$.subscribe((state) => {
      if (state.type === LoginState.accountDetected) {
        this.currentState = state as AccountDetectedState;
      }
    });
  }

  formatLastLogin = (lastLogin?: string | null) => {
    if (lastLogin == null) return 'Nunca';

    const now = new Date().getTime();
    const lastLoginDate = new Date(lastLogin).getTime();
    const differenceMs = now - lastLoginDate;

    const days = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(differenceMs / (1000 * 60 * 60));
    const minutes = Math.floor(differenceMs / (1000 * 60));

    if (days > 0) {
      return `Hace ${days} día${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
      return `Hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    } else {
      return 'Hace un momento';
    }
  };

  sendOtp(): void {
    this.otpSent.set(true);

    this.emailValidationService
      .generateOtp({
        tipoVerificacion: TipoVerificacionEnum.VINCULAR_TELEFONO,
        destino: this.currentState?.foundPhoneData?.idEmail.toString() || '',
      })
      .subscribe({
        next: (response) => {
          if (response?.success && response?.data) {
            this.idVerificacionVinculacion.set(response.data.idVerificacion);
          }
        },
        error: (error) => {
          this.otpSent.set(false);
        },
      });
  }

  mostrarOpciones(): void {
    this.otpSent.set(false);
    this.idVerificacionVinculacion.set(0);
  }

  unirCuentas(otpCode: string): void {
    this.isProcessingRegister.set(true);
    this.emailValidationService
      .register({
        nombre: this.currentState?.loginFlow.name || '',
        idEmail: this.currentState?.foundPhoneData?.idEmail || 0,
        email: this.currentState?.loginFlow.email || '',
        prefijoTelefono: this.currentState?.loginFlow.prefijoTelefono || '',
        telefono: this.currentState?.loginFlow.telefono || '',
        idProveedor: this.currentState?.loginFlow.idProveedor || 0,
        modoRegistro: ModoRegistroEnum.UNIR_CUENTAS,
        password: this.currentState?.loginFlow.password || '',
        idToken: this.currentState?.loginFlow.idToken || '',
        idUsuarioProveedor:
          this.currentState?.loginFlow.idUsuarioProveedor || '',
        otp: otpCode,
        idVerificacion: this.idVerificacionVinculacion(),
        photoUrl: this.currentState?.loginFlow.photoUrl || '',
      })
      .subscribe({
        next: (response) => {
          if (response?.success && response?.data) {
            const castedResponse = response.data as RegisterResponse;
            this.loginService.notifyLoginState({
              type: LoginState.finish,
              success: true,
              timestamp: new Date(),
              user: castedResponse?.user || {},
              loginFlow: {
                name: this.currentState?.loginFlow.name || '',
                email: this.currentState?.loginFlow.email || '',
                idProveedor:
                  this.currentState?.loginFlow.idProveedor ||
                  TipoProveedorOAuthEnum.CREDENCIAL,
                idToken: this.currentState?.loginFlow.idToken || '',
              },
              redirectUrl: this.currentState?.loginFlow.redirectUrl || '',
            });
            this.isProcessingRegister.set(false);
          }
        },
        error: (error) => {
          console.error(error);
          this.isProcessingRegister.set(false);
        },
      });
  }

  reemplazarCuenta(): void {
    this.isProcessingRegister.set(true);
    this.emailValidationService
      .register({
        nombre: this.currentState?.loginFlow.name || '',
        idEmail: this.currentState?.foundPhoneData?.idEmail || 0,
        email: this.currentState?.loginFlow.email || '',
        prefijoTelefono: this.currentState?.loginFlow.prefijoTelefono || '',
        telefono: this.currentState?.loginFlow.telefono || '',
        idProveedor: this.currentState?.loginFlow.idProveedor || 0,
        modoRegistro: ModoRegistroEnum.REEMPLAZAR_CUENTA,
        password: this.currentState?.loginFlow.password || '',
        idToken: this.currentState?.loginFlow.idToken || '',
        idUsuarioProveedor:
          this.currentState?.loginFlow.idUsuarioProveedor || '',
        otp: null,
        idVerificacion: null,
        photoUrl: this.currentState?.loginFlow.photoUrl || null,
      })
      .subscribe({
        next: (response) => {
          if (response?.success && response?.data) {
            const castedResponse = response.data as RegisterResponse;
            this.loginService.notifyLoginState({
              type: LoginState.finish,
              success: true,
              timestamp: new Date(),
              user: castedResponse?.user || {},
              loginFlow: {
                name: this.currentState?.loginFlow.name || '',
                email: this.currentState?.loginFlow.email || '',
                idProveedor:
                  this.currentState?.loginFlow.idProveedor ||
                  TipoProveedorOAuthEnum.CREDENCIAL,
                idToken: this.currentState?.loginFlow.idToken || '',
              },
              redirectUrl: this.currentState?.loginFlow.redirectUrl,
            });
            this.isProcessingRegister.set(false);
          }
        },
        error: (error) => {
          console.error(error);
          this.isProcessingRegister.set(false);
        },
      });
  }
}
